import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useMainContext } from "../../context/MainContext";
import { sendData } from "../../services/sendData";
import { stdObject } from "../../data/types/stdObject";
import {
  ActiveChatTarget,
  ChatConversation,
  ChatMessage,
  ChatReaction,
} from "./chatTypes";

const mergeMessages = (prev: ChatMessage[], incoming: ChatMessage[]) => {
  const map = new Map(prev.map((m) => [m.CODCHA, m]));
  incoming.forEach((m) => map.set(m.CODCHA, m));
  return Array.from(map.values()).sort((a, b) => a.CODCHA - b.CODCHA);
};

export const useChatModal = (open: boolean) => {
  const { logoutAction } = useMainContext();
  const user: any = useSelector((state: RootState) => state.data.user);

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [activeTarget, setActiveTarget] = useState<ActiveChatTarget | null>(
    null,
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(
    null,
  );

  const activeTargetRef = useRef<ActiveChatTarget | null>(null);
  useEffect(() => {
    activeTargetRef.current = activeTarget;
  }, [activeTarget]);

  const startSendData = useCallback(
    async (endPoint: string, payload: stdObject = {}) =>
      await sendData({ endPoint, payload, logoutAction }),
    [logoutAction],
  );

  const fetchConversations = useCallback(async () => {
    const result = await startSendData("listChatConversations");
    if (result.success && result.status) {
      setConversations(result.conversations || []);
    }
  }, [startSendData]);

  const fetchThread = useCallback(
    async (target: ActiveChatTarget, beforeId?: number) => {
      if (target.type === "role") {
        setMessages([]);
        setHasMoreMessages(false);
        return;
      }
      const payload: stdObject =
        target.type === "roleBroadcast"
          ? { partnerType: "roleBroadcast", groupId: target.groupId }
          : { partnerType: "user", partnerCode: target.code, beforeId };
      const result = await startSendData("getChatThread", payload);
      if (result.success && result.status) {
        const reactionsByMessage = new Map<number, ChatReaction[]>();
        (result.reactions || []).forEach((r: ChatReaction & { CODCHA: number }) => {
          const list = reactionsByMessage.get(r.CODCHA) || [];
          list.push({ CODUSU: r.CODUSU, EMOCHA: r.EMOCHA, NOMUSU: r.NOMUSU });
          reactionsByMessage.set(r.CODCHA, list);
        });
        const incoming: ChatMessage[] = (result.messages || []).map(
          (m: ChatMessage) => ({
            ...m,
            reactions: reactionsByMessage.get(m.CODCHA) || [],
          }),
        );
        setMessages((prev) => mergeMessages(prev, incoming));
        setHasMoreMessages(!!result.hasMore);
        if (target.type === "user") {
          startSendData("markChatRead", { partnerCode: target.code });
        }
      }
    },
    [startSendData],
  );

  // conversation list poll — only while the panel is open
  const conversationsEpochRef = useRef(0);
  const conversationsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(() => {
    if (!open) return;
    const epoch = ++conversationsEpochRef.current;

    const schedule = () => {
      if (epoch !== conversationsEpochRef.current) return;
      conversationsTimerRef.current = setTimeout(poll, 12000);
    };
    const poll = async () => {
      if (epoch !== conversationsEpochRef.current) return;
      await fetchConversations();
      schedule();
    };

    setLoadingConversations(true);
    fetchConversations().finally(() => setLoadingConversations(false));
    schedule();

    return () => {
      conversationsEpochRef.current++;
      if (conversationsTimerRef.current) {
        clearTimeout(conversationsTimerRef.current);
        conversationsTimerRef.current = null;
      }
    };
  }, [open, fetchConversations]);

  // active thread poll — only while a real (already-sent) target is open
  const threadEpochRef = useRef(0);
  const threadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!open || !activeTarget || activeTarget.type === "role") return;
    const target = activeTarget;
    const epoch = ++threadEpochRef.current;

    const schedule = () => {
      if (epoch !== threadEpochRef.current) return;
      threadTimerRef.current = setTimeout(poll, 3000);
    };
    const poll = async () => {
      if (epoch !== threadEpochRef.current) return;
      await fetchThread(target);
      schedule();
    };

    setMessages([]);
    setLoadingMessages(true);
    fetchThread(target).finally(() => setLoadingMessages(false));
    schedule();

    return () => {
      threadEpochRef.current++;
      if (threadTimerRef.current) {
        clearTimeout(threadTimerRef.current);
        threadTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTarget?.type, activeTarget?.code, activeTarget?.groupId]);

  const openConversation = useCallback((conversation: ChatConversation) => {
    setComposerText("");
    setSendError(false);
    setEditingMessageId(null);
    setActiveTarget({
      type: conversation.type,
      code: conversation.code,
      groupId: conversation.groupId,
      name: conversation.name,
    });
  }, []);

  const selectNewChatTarget = useCallback(
    (type: "user" | "role", code: number, name: string) => {
      setPickerOpen(false);
      setComposerText("");
      setSendError(false);
      setEditingMessageId(null);
      setActiveTarget({ type, code, groupId: null, name });
    },
    [],
  );

  const closeThread = useCallback(() => setActiveTarget(null), []);

  const loadOlderMessages = useCallback(() => {
    const target = activeTargetRef.current;
    if (!target || target.type !== "user" || messages.length === 0) return;
    fetchThread(target, messages[0].CODCHA);
  }, [messages, fetchThread]);

  const reactToMessage = useCallback(
    async (codCha: number, emoji: string) => {
      const result = await startSendData("reactChatMessage", {
        codCha,
        emoji,
      });
      if (result.success && result.status) {
        const target = activeTargetRef.current;
        if (target) fetchThread(target);
      }
    },
    [startSendData, fetchThread],
  );

  const removeReaction = useCallback(
    async (codCha: number) => {
      const result = await startSendData("reactChatMessage", {
        codCha,
        remove: true,
      });
      if (result.success && result.status) {
        const target = activeTargetRef.current;
        if (target) fetchThread(target);
      }
    },
    [startSendData, fetchThread],
  );

  const startEditMessage = useCallback((message: ChatMessage) => {
    setEditingMessageId(message.CODCHA);
    setComposerText(message.MSGCHA);
    setSendError(false);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setComposerText("");
    setSendError(false);
  }, []);

  const deleteMessage = useCallback(
    async (codCha: number) => {
      const result = await startSendData("deleteChatMessage", { codCha });
      if (result.success && result.status) {
        const target = activeTargetRef.current;
        if (target) fetchThread(target);
        fetchConversations();
      }
    },
    [startSendData, fetchThread, fetchConversations],
  );

  const sendMessage = useCallback(async () => {
    const target = activeTargetRef.current;
    const body = composerText.trim();
    if (!target || body === "" || sending) return;
    setSending(true);
    setSendError(false);
    try {
      if (editingMessageId !== null) {
        const result = await startSendData("editChatMessage", {
          codCha: editingMessageId,
          body,
        });
        if (result.success && result.status) {
          setEditingMessageId(null);
          setComposerText("");
          await fetchThread(target);
          fetchConversations();
        } else {
          setSendError(true);
        }
        return;
      }

      const isRole = target.type === "role" || target.type === "roleBroadcast";
      const result = await startSendData("sendChatMessage", {
        targetType: isRole ? "role" : "user",
        targetCode: target.code,
        body,
      });
      if (result.success && result.status) {
        setComposerText("");
        if (isRole) {
          setActiveTarget({
            type: "roleBroadcast",
            code: target.code,
            groupId: result.groupId,
            name: target.name,
          });
        } else {
          await fetchThread(target);
        }
        fetchConversations();
      } else {
        setSendError(true);
      }
    } finally {
      setSending(false);
    }
  }, [
    composerText,
    sending,
    editingMessageId,
    startSendData,
    fetchThread,
    fetchConversations,
  ]);

  return {
    me: Number(user?.CODUSU) || 0,
    conversations,
    loadingConversations,
    activeTarget,
    messages,
    loadingMessages,
    hasMoreMessages,
    composerText,
    setComposerText,
    sending,
    sendError,
    pickerOpen,
    setPickerOpen,
    openConversation,
    selectNewChatTarget,
    closeThread,
    loadOlderMessages,
    sendMessage,
    reactToMessage,
    removeReaction,
    editingMessageId,
    startEditMessage,
    cancelEdit,
    deleteMessage,
  };
};
